import { BasicContainer, FlexCol, FlexRow, H1Text } from '@minecraft.atoms';
import React, { FC, useMemo } from 'react';
import { useTranslation } from '@blocs.i18n';
import { gqlAccount } from '@minecraft.graphql-types';
import { DiscountTypeEnum } from '@minecraft.graphql-operations';
import { CheckoutHead } from './CheckoutHead';
import { PlanType } from '../usePlans';
import { CheckoutProps } from './types';
import { PlanFeatureCardList } from './PlanFeatureCard';
import { PlanExplorer } from './PlanExplorer';
import { RegistrationTestimonial } from './RegistrationTestimonial';

type DesktopRegistrationUpsellProps = {
  setIsAnnual: (isAnnual: boolean) => void;
  isAnnual: boolean;
  selectedPlan?: PlanType;
  plans: PlanType[];
  countryCode: gqlAccount['country']['code'];
  currencyCode: string;
  isToggleLocked?: boolean;
  disableTrial?: boolean;
  onCompletePurchase: CheckoutProps['onCompletePurchase'];
  onPaymentCompleteRedirect: CheckoutProps['onPaymentCompleteRedirect'];
  setSelectedPlan: CheckoutProps['setSelectedPlan'];
  discountType?: CheckoutProps['discountType'];
};

export const DesktopRegistrationUpsell: FC<DesktopRegistrationUpsellProps> = React.memo(
  ({
    setIsAnnual,
    isAnnual,
    plans,
    countryCode,
    currencyCode,
    isToggleLocked,
    selectedPlan,
    setSelectedPlan,
    disableTrial,
    discountType,
  }) => {
    const { t } = useTranslation();
    const isReppedRegDiscount = discountType === DiscountTypeEnum.ReppedRegistrationDiscount;

    const orderedHighlightedFeatures = useMemo(() => {
      if (!selectedPlan) return [];
      const keyFeatureSet = new Set(selectedPlan.keyFeatures.map((f) => f.code));
      return selectedPlan.features.filter((feature) => !keyFeatureSet.has(feature.code));
    }, [selectedPlan]);

    return (
      <FlexCol justifyContent="start" alignItems="center" w="full" gap="6" maxW="2xl" mx="auto">
        <CheckoutHead
          isAnnual={isAnnual}
          setIsAnnual={setIsAnnual}
          isToggleLocked={isToggleLocked}
          plans={plans}
          countryCode={countryCode}
          currencyCode={currencyCode}
        />
        <FlexCol w="full" gap="4" alignItems="center">
          <BasicContainer w="full" mt="4" mx="auto">
            <FlexCol w="full" gap="4" justifyContent="center" alignItems="center">
              <FlexRow gap="2" maxW="md">
                <H1Text fontWeight="semibold" lineHeight="expanded" align="center">
                  {t('common:paywall.plan.upsell.title')}
                </H1Text>
              </FlexRow>
              <FlexCol gap="8" w="full" justifyContent="center" alignItems="center" m="auto" mt="4">
                <PlanFeatureCardList
                  plans={plans}
                  selectedPlan={selectedPlan}
                  setSelectedPlan={setSelectedPlan}
                  isReppedRegDiscount={isReppedRegDiscount}
                  countryCode={countryCode}
                  disableTrial={disableTrial}
                />
                {!isReppedRegDiscount && (
                  <PlanExplorer
                    selectedPlan={selectedPlan}
                    customFeatures={orderedHighlightedFeatures}
                    ctaText={t('common:paywall.plan.features.exploreAdditionalFeatures')}
                  />
                )}
              </FlexCol>
            </FlexCol>
          </BasicContainer>
        </FlexCol>
        {isReppedRegDiscount && <RegistrationTestimonial />}
      </FlexCol>
    );
  }
);
